// components/layout/Header.js
// Header — semi-transparent, backdrop-blur, glassmorphism
//
// תוכן (RTL — מימין לשמאל):
// ימין: hamburger (mobile) + breadcrumb placeholder
// שמאל: notification bell + badge, user avatar + menu

import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  IconButton,
  Badge,
  Avatar,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Box,
  Typography,
  Popover,
  List,
  ListItem,
  ListItemButton,
  Divider,
  Button,
  Alert,
  Chip,
  CircularProgress,
  useTheme,
  useMediaQuery,
} from '@mui/material';

// Icons
import MenuIcon from '@mui/icons-material/Menu';
import NotificationsIcon from '@mui/icons-material/NotificationsOutlined';
import PaletteIcon from '@mui/icons-material/PaletteOutlined';
import AccessibilityNewIcon from '@mui/icons-material/AccessibilityNew';
import LogoutIcon from '@mui/icons-material/LogoutOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import CircleIcon from '@mui/icons-material/Circle';
import LockResetIcon from '@mui/icons-material/LockReset';

import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { useNotifications } from '../../contexts/NotificationContext';
import { useThemeContext } from '../../contexts/ThemeContext';
import { themeList } from '../../config/themes';
import ChangePasswordDialog from '../shared/ChangePasswordDialog';

// =============================================================================
// Header Component
// =============================================================================
export default function Header({ onMobileMenuToggle, sidebarWidth }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery('(max-width:767px)');

  const { user, isDefaultPassword, isAdmin, isSuperAdmin, logout } = useAuth();
  const { unreadCount, recentAlerts, isLoadingAlerts, fetchRecentAlerts, markAsRead, markAllRead } = useNotifications();
  const { themeKey, setThemeKey } = useThemeContext();

  // Menu anchors
  const [userMenuAnchor, setUserMenuAnchor] = useState(null);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const [notifAnchor, setNotifAnchor] = useState(null);
  const [themeAnchor, setThemeAnchor] = useState(null);

  // Header glassmorphism styles from theme
  const headerStyles = theme.custom?.glassmorphism?.header || {};

  // ===================================================================
  // Notification bell click — fetch recent + open dropdown
  // ===================================================================
  const handleNotifOpen = useCallback((e) => {
    setNotifAnchor(e.currentTarget);
    fetchRecentAlerts();
  }, [fetchRecentAlerts]);

  const handleNotifClose = useCallback(() => {
    setNotifAnchor(null);
  }, []);

  // ===================================================================
  // User menu
  // ===================================================================
  const handleUserMenuOpen = useCallback((e) => {
    setUserMenuAnchor(e.currentTarget);
  }, []);

  const handleUserMenuClose = useCallback(() => {
    setUserMenuAnchor(null);
  }, []);

  const handleLogout = useCallback(async () => {
    handleUserMenuClose();
    await logout();
  }, [logout, handleUserMenuClose]);

  // ===================================================================
  // Theme picker
  // ===================================================================
  const handleThemeOpen = useCallback((e) => {
    handleUserMenuClose();
    setThemeAnchor(e.currentTarget || document.body);
  }, [handleUserMenuClose]);

  const handleThemeClose = useCallback(() => {
    setThemeAnchor(null);
  }, []);

  // ===================================================================
  // Accessibility statement
  // ===================================================================
  const handleAccessibility = useCallback(() => {
    handleUserMenuClose();
    navigate('/accessibility-statement');
  }, [navigate, handleUserMenuClose]);

  // Role label
  const roleLabel = isSuperAdmin
    ? 'Super Admin'
    : isAdmin
      ? 'Admin'
      : 'Viewer';

  return (
    <>
      <AppBar
        data-testid="layout__header"
        position="fixed"
        elevation={0}
        sx={{
          width: isMobile ? '100%' : `calc(100% - ${sidebarWidth}px)`,
          // RTL: sidebar בצד ימין ויזואלית. ml בקוד → margin-right ויזואלית (stylis flip)
          ml: isMobile ? 0 : `${sidebarWidth}px`,
          ...headerStyles,
          bgcolor: headerStyles.background || 'rgba(255,255,255,0.85)',
          color: theme.palette.text.primary,
          zIndex: theme.zIndex.drawer + 1,
        }}
      >
        <Toolbar sx={{ gap: 1, minHeight: { xs: 56, sm: 64 } }}>
          {/* Mobile hamburger */}
          {isMobile && (
            <IconButton
              data-testid="layout__button--hamburger"
              edge="start"
              color="inherit"
              aria-label={t('nav.dashboard')}
              onClick={onMobileMenuToggle}
              sx={{ ml: -1 }}
            >
              <MenuIcon />
            </IconButton>
          )}

          {/* Spacer */}
          <Box sx={{ flex: 1 }} />

          {/* Notification bell */}
          <IconButton
            data-testid="layout__button--notifications"
            color="inherit"
            aria-label={t('notifications.title')}
            onClick={handleNotifOpen}
          >
            <Badge
              badgeContent={unreadCount}
              color="error"
              max={99}
              data-testid="layout__badge--notifications"
            >
              <NotificationsIcon />
            </Badge>
          </IconButton>

          {/* User avatar + menu */}
          <IconButton
            data-testid="layout__button--user-menu"
            onClick={handleUserMenuOpen}
            sx={{ p: 0.5 }}
            aria-label={user?.full_name || t('nav.profile')}
          >
            <Avatar
              sx={{
                width: 36,
                height: 36,
                bgcolor: theme.palette.primary.main,
                fontSize: '0.875rem',
                fontWeight: 600,
              }}
            >
              {user?.full_name?.charAt(0) || 'U'}
            </Avatar>
          </IconButton>
        </Toolbar>

        {/* Default password warning bar */}
        {isDefaultPassword && (
          <Alert
            data-testid="layout__alert--default-password"
            severity="warning"
            icon={<WarningAmberIcon />}
            action={
              <Button
                data-testid="layout__button--change-password"
                color="inherit"
                size="small"
                onClick={() => setChangePasswordOpen(true)}
              >
                {t('defaultPassword.changeNow')}
              </Button>
            }
            sx={{
              borderRadius: 0,
              py: 0.25,
              '& .MuiAlert-message': { py: 0.5 },
            }}
          >
            {isSuperAdmin
              ? t('defaultPassword.warningSuperAdmin')
              : t('defaultPassword.warning')
            }
          </Alert>
        )}
      </AppBar>

      {/* ================================================================= */}
      {/* Notification Dropdown                                              */}
      {/* ================================================================= */}
      <Popover
        data-testid="layout__popover--notifications"
        open={Boolean(notifAnchor)}
        anchorEl={notifAnchor}
        onClose={handleNotifClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: { sx: { width: 360, maxHeight: 440, borderRadius: 3 } },
        }}
      >
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.5 }}>
          <Typography variant="subtitle1" fontWeight={600}>
            {t('notifications.title')}
          </Typography>
          {unreadCount > 0 && (
            <Button
              data-testid="layout__button--mark-all-read"
              size="small"
              startIcon={<DoneAllIcon />}
              onClick={async () => {
                await markAllRead();
              }}
            >
              {t('notifications.markAllRead')}
            </Button>
          )}
        </Box>

        <Divider />

        {/* Alert list */}
        {isLoadingAlerts ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={28} />
          </Box>
        ) : recentAlerts.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4, px: 2 }}>
            <NotificationsIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
            <Typography variant="body2" color="text.secondary">
              {t('notifications.noNotifications')}
            </Typography>
          </Box>
        ) : (
          <List disablePadding sx={{ maxHeight: 300, overflow: 'auto' }}>
            {recentAlerts.map((alert) => (
              <ListItem key={alert.id} disablePadding>
                <ListItemButton
                  data-testid={`layout__notification--${alert.id}`}
                  onClick={async () => {
                    if (!alert.is_read) {
                      await markAsRead(alert.id);
                    }
                    handleNotifClose();
                  }}
                  sx={{
                    px: 2,
                    py: 1.5,
                    bgcolor: alert.is_read ? 'transparent' : 'action.hover',
                  }}
                >
                  <Box sx={{ width: '100%' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      {!alert.is_read && (
                        <CircleIcon sx={{ fontSize: 8, color: 'primary.main' }} />
                      )}
                      <Chip
                        label={t(`dashboard.alertTypes.${alert.alert_type}`)}
                        size="small"
                        color={
                          alert.alert_type === 'low_score' ? 'error'
                            : alert.alert_type === 'deadline' ? 'warning'
                              : 'info'
                        }
                        sx={{ height: 20, fontSize: '0.7rem' }}
                      />
                      <Typography variant="caption" color="text.disabled" sx={{ mr: 'auto' }}>
                        {alert.unit_name}
                      </Typography>
                    </Box>
                    {/* ★ alert_message — לא message! */}
                    <Typography variant="body2" noWrap>
                      {alert.alert_message}
                    </Typography>
                  </Box>
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        )}

        <Divider />

        {/* View all */}
        <Box sx={{ textAlign: 'center', py: 1 }}>
          <Button
            data-testid="layout__button--view-all-notifications"
            size="small"
            onClick={() => {
              handleNotifClose();
              // TODO v0.4.0: navigate to notifications/alerts page
            }}
          >
            {t('notifications.viewAll')}
          </Button>
        </Box>
      </Popover>

      {/* ================================================================= */}
      {/* User Menu                                                          */}
      {/* ================================================================= */}
      <Menu
        data-testid="layout__menu--user"
        anchorEl={userMenuAnchor}
        open={Boolean(userMenuAnchor)}
        onClose={handleUserMenuClose}
        transformOrigin={{ horizontal: 'left', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
        slotProps={{
          paper: { sx: { width: 240, borderRadius: 2, mt: 0.5 } },
        }}
      >
        {/* User info header */}
        <Box sx={{ px: 2, py: 1.5 }}>
          <Typography variant="subtitle2" fontWeight={600}>
            {user?.full_name || ''}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {roleLabel}
          </Typography>
        </Box>

        <Divider sx={{ my: 0.5 }} />

        {/* Theme picker */}
        <MenuItem
          data-testid="layout__button--theme-picker"
          onClick={handleThemeOpen}
        >
          <ListItemIcon><PaletteIcon fontSize="small" /></ListItemIcon>
          <ListItemText>{t('nav.theme')}</ListItemText>
        </MenuItem>

        {/* Accessibility statement */}
        <MenuItem
          data-testid="layout__menu-item--accessibility"
          onClick={handleAccessibility}
        >
          <ListItemIcon><AccessibilityNewIcon fontSize="small" /></ListItemIcon>
          <ListItemText>{t('nav.accessibilityStatement')}</ListItemText>
        </MenuItem>

        <Divider sx={{ my: 0.5 }} />

        {/* Change Password */}
        <MenuItem
          data-testid="layout__menu-item--change-password"
          onClick={() => {
            setUserMenuAnchor(null);
            setChangePasswordOpen(true);
          }}
        >
          <ListItemIcon><LockResetIcon fontSize="small" /></ListItemIcon>
          <ListItemText>{t('users.changePassword')}</ListItemText>
        </MenuItem>

        {/* Logout */}
        <MenuItem
          data-testid="layout__menu-item--logout"
          onClick={handleLogout}
        >
          <ListItemIcon><LogoutIcon fontSize="small" color="error" /></ListItemIcon>
          <ListItemText sx={{ color: 'error.main' }}>{t('nav.logout')}</ListItemText>
        </MenuItem>
      </Menu>

      {/* ================================================================= */}
      {/* Theme Picker Popover                                               */}
      {/* ================================================================= */}
      <Popover
        data-testid="layout__popover--theme-picker"
        open={Boolean(themeAnchor)}
        anchorEl={themeAnchor}
        onClose={handleThemeClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        slotProps={{
          paper: { sx: { p: 2.5, borderRadius: 3, width: 340 } },
        }}
      >
        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
          {t('themes.title')}
        </Typography>

        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1.5 }}>
          {themeList.map((th) => (
            <Box
              key={th.key}
              data-testid={`layout__theme-option--${th.key}`}
              onClick={() => {
                setThemeKey(th.key);
                handleThemeClose();
              }}
              sx={{
                cursor: 'pointer',
                borderRadius: 2,
                overflow: 'hidden',
                border: th.key === themeKey ? `2px solid ${theme.palette.primary.main}` : '2px solid transparent',
                transition: 'all 0.2s ease',
                '&:hover': {
                  transform: 'scale(1.05)',
                  boxShadow: 3,
                },
              }}
            >
              {/* Color preview */}
              <Box
                sx={{
                  height: 48,
                  background: `linear-gradient(135deg, ${th.previewColors.primary} 0%, ${th.previewColors.background} 100%)`,
                  position: 'relative',
                }}
              >
                {th.key === themeKey && (
                  <Chip
                    label={t('themes.current')}
                    size="small"
                    color="primary"
                    sx={{
                      position: 'absolute',
                      top: 4,
                      // Logical property — לא מתהפך ע"י stylis-plugin-rtl
                      insetInlineEnd: 4,
                      height: 18,
                      fontSize: '0.6rem',
                    }}
                  />
                )}
              </Box>
              <Box sx={{ p: 0.75, textAlign: 'center', bgcolor: th.previewColors.background }}>
                <Typography
                  variant="caption"
                  fontWeight={th.key === themeKey ? 700 : 400}
                  sx={{ color: th.previewColors.text, fontSize: '0.7rem' }}
                >
                  {t(`themes.${th.key}`)}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      </Popover>

      {/* Change Password Dialog */}
      <ChangePasswordDialog
        open={changePasswordOpen}
        onClose={() => setChangePasswordOpen(false)}
      />
    </>
  );
}
